import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import Button from "../../components/Button";
import CopyableField from "./components/CopyableField";
import AssignForm from "./components/AssignForm";
import AdminNavBar from "./components/AdminNavBar";

import {
    getLicenseSummary,
    assignLicense,
    unassignLicense,
    resendLicenseEmail,
} from "./api/licenseRequests";
import { useTranslation } from "../../i18n/TranslationProvider";


const AssignLicenses = () => {
    const navigate = useNavigate();
    const { userId } = useAuth();

    const [licenseKey, setLicenseKey] = useState("");
    const [totalPurchased, setTotalPurchased] = useState(0);
    const [expiresAt, setExpiresAt] = useState("");
    const [assigned, setAssigned] = useState([]);
    const [availableLicenses, setAvailableLicenses] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const { t } = useTranslation();

    const [showAssignForm, setShowAssignForm] = useState(false);
    // for showing message
    const [confirmedEmail, setConfirmedEmail] = useState("");
    const [resentIds, setResentIds] = useState([]);

    // for showing message
    const [unassignedEmail, setUnassignedEmail] = useState("");

   useEffect(() => {
    const fetchSummary = async () => {
        setLoading(true);
        setLoadError("");

        try {
            const res = await getLicenseSummary(userId);

            setLicenseKey(res.data.licenseKey);
            setTotalPurchased(res.data.totalPurchased);
            setExpiresAt(
                new Date(res.data.expiresAt).toLocaleDateString()
            );
            setAssigned(res.data.assigned);
            setAvailableLicenses(res.data.availableCount);
        } catch (err) {
            console.error("Error fetching license summary:", {
                status: err.response?.status,
                data: err.response?.data,
                url: err.config?.url,
            });

            setLoadError(
                err.response?.data?.message ||
                    "Unable to load license information."
            );
        } finally {
            setLoading(false);
        }
    };

    if (userId) {
        fetchSummary();
    }
}, [userId]);

const handleUnassign = async (id) => {
    const target = assigned.find((a) => a.id === id);

    try {
        await unassignLicense(id);

        setAssigned((prev) => prev.filter((a) => a.id !== id));
        setAvailableLicenses((prev) => prev + 1);

        if (target) {
            setUnassignedEmail(target.email);
            setTimeout(() => setUnassignedEmail(""), 3000);
        }
    } catch (err) {
        console.error("Error unassigning license", err);
    }
};

const handleResendEmail = async (id) => {
    setResentIds((prev) => [...prev, id]);

    try {
        await resendLicenseEmail(id);
    } catch (err) {
        console.error("Error resending license email", err);
    } finally {
        setTimeout(() => {
            setResentIds((prev) => prev.filter((i) => i !== id));
        }, 2000);
    }
};
const handleAssign = async (email) => {
    const { data } = await assignLicense(userId, email);

    setAssigned((prev) => [...prev, data]);
    setAvailableLicenses((prev) => prev - 1);

    setShowAssignForm(false);
    setConfirmedEmail(email);

    setTimeout(() => setConfirmedEmail(""), 3000);
};

    const handleOpenAssignForm = () => {
        setConfirmedEmail("");
        setShowAssignForm(true);
    };

    if (loading) {
        return (
            <h6 className="text-center text-lg font-normal">Loading...</h6>
        );
    }

    if (loadError) {
        return (
            <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 text-center">
                <p className="text-red-500">{loadError}</p>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/athletes")}
                >
                    {"< Back to Roster"}
                </Button>
            </div>
        );
    }

    return (
        <>

            <AdminNavBar />
            <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 !rounded-tl-none">
                <h4 className="text-lg md:text-2xl text-center mx-auto">
                    {t("athletes.assignLicenses")}
                </h4>
                <h6 className="m-4 text-md">{t("licenses.yourLicenseKeyLabel")}</h6>

                <CopyableField value={licenseKey} />
                <p className="italic mt-4 mb-6 text-center text-sm">
                    {t("licenses.keyExplanationLine1")}
                    <br /> {t("licenses.keyExplanationLine2")}
                </p>

                <hr className="flex-grow border-t border-gray-300 m-6"></hr>
                <h6 className="m-4 mt-6 text-md">{t("licenses.allocationHeading")}</h6>

                <p className="italic mt-4 mb-6 text-center">
                    {t("licenses.purchasedPrefix")} <span className="text-teal-600 font-semibold text-lg">{totalPurchased}</span> {t("licenses.licensesSuffix")} <br />
                    {t("licenses.expiryPrefix")} <span className="text-teal-600 font-semibold text-lg">{expiresAt}</span>.
                </p>

                <div className="grid grid-cols-2 gap-6">
                    {/* Assigned column */}
                    <div className="border-r border-gray-300 pr-6">
                        <h6 className="text-center font-semibold text-lg mb-4">
                            {t("licenses.assignedLabel")} <span className="text-gray-400">({assigned.length})</span>
                        </h6>
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                            {assigned.length === 0 ? (
                                <p className="text-center text-gray-400 text-sm">
                                    {t("licenses.noLicensesAssigned")}
                                </p>
                            ) : (
                                assigned.map((a) => (
                                    <div
                                        key={a.id}
                                        className="bg-gray-100 rounded-lg p-3"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm truncate">
                                                {a.email}
                                            </span>
                                            <button
                                                onClick={() =>
                                                    handleUnassign(a.id)
                                                }
                                                className="shrink-0  bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-semibold rounded-full px-3 py-1.5"
                                            >
                                                {t("licenses.unassignButton")}
                                            </button>
                                        </div>
                                        {!a.activated && (
                                            <div className="flex items-center justify-between gap-2 mt-2">
                                                <span className="text-red-400 text-sm italic">
                                                    {t("licenses.unactivatedLabel")}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        handleResendEmail(
                                                            a.id
                                                        )
                                                    }
                                                    disabled={resentIds.includes(
                                                        a.id
                                                    )}
                                                    className="shrink-0 bg-gray-500 hover:bg-gray-600 disabled:bg-teal-600/70 text-white text-xs font-semibold rounded-full px-3 py-1.5 min-w-[92px]"
                                                >
                                                    {resentIds.includes(a.id)
                                                        ? t("licenses.sentLabel")
                                                        : t("licenses.resendEmailButton")}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Unassigned column */}
                    <div className="flex flex-col items-center justify-start pt-2">
                        <h6 className="text-center font-semibold text-lg mb-4">
                            {t("licenses.unassignedHeading")}
                        </h6>
                        <span className="text-teal-600 font-bold text-4xl">
                            {availableLicenses}
                        </span>
                        <p className="text-sm mb-6">{t("licenses.availableLicensesLabel")}</p>
                        <button
                            onClick={handleOpenAssignForm}
                            disabled={
                                showAssignForm || availableLicenses === 0
                            }
                            className="bg-cyan-700 hover:bg-cyan-800 disabled:bg-cyan-700/50 disabled:cursor-not-allowed text-white font-semibold rounded-full px-8 py-2.5 mb-4"
                        >
                            {t("licenses.assignButton")}
                        </button>

                        {showAssignForm && (
                            <AssignForm
                                existingEmails={assigned.map((a) => a.email)}
                                onSend={handleAssign}
                            />
                        )}

                        {confirmedEmail && (
                            <p className="text-teal-600 text-sm text-center mt-2">
                                {t("licenses.sentToEmail", { email: confirmedEmail })}
                            </p>
                        )}

                        {unassignedEmail && (
                            <p className="text-teal-600 text-sm text-center mt-2">
                                {t("licenses.emailWasUnassigned", { email: unassignedEmail })}
                            </p>
                        )}
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/athletes")}
                >
                    {"< Back to Roster"}
                </Button>
            </div>
        </>
    );
};

export default AssignLicenses;